import { useLoaderData, useRouter, useSearch } from "@tanstack/react-router";
import "./../../styles/embeddedHtmlStyles.scss";
import type { StudyWorkshopDetails } from "../../api/studyWorkshopDetails.types";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import {
	useWebinarSubscription,
	useCourseSubscription,
} from "@/hooks/useWorkshopSubscription";
import { determineAccessState } from "./workshopStudy_access_state";
import { Toast } from "@capacitor/toast";
import { LoaderSpinner } from "../../components/ui/LoaderSpinner";
import { Skeleton } from "../../components/ui/skeleton";
import { TrainingFile } from "../../components/shared/TrainingFile";
import { AuthorsSimplyList } from "@/components/shared/AuthorsSimplyList";
import { chooseCoverDate } from "@/utils/dateAndTime";
import { MultimediaCard } from "./MultimediaCard";
import { CoursesCertificats } from "./CoursesCertificats";
import type { StudyStatus } from "@/routes/(app)/_app.study.$studyId";
import { setBreadcrumb } from "./helpers";
import CoverCardNewVisual from "../../components/shared/CoverCardNewVisual";
import { ExternalHtml } from "@/components/ExternalHtml";
import { useQuery } from "@tanstack/react-query";
import { getProfileOptions } from "@/api/queryOptions";
import { Browser } from "@capacitor/browser";
import { getWebinarRedirectData } from "@/api/api";
import { CheckIcon } from "@/components/icons/check";
import { ArrowIcon } from "@/components/icons/arrow";

// const WhatDidYouLearn = ({ data }: { data: string[] }) => {
// 	return (
// 		<div className="flex flex-col gap-4">
// 			<h2 className="text-2xl font-bold pb-4 pr-7">Czego się nauczysz?</h2>
// 			{data.map((el) => {
// 				return (
// 					<div className="flex">
// 						<img className="w-5 h-5 mr-3" src={check} />
// 						<p>{el}</p>
// 					</div>
// 				);
// 			})}
// 		</div>
// 	);
// };
// const WHAT_DID_YOU_LEARN_MOCK = [
// 	"31 grudnia - inwentura wg zasad kasowego PIT/niekasowego PIT",
// 	"terminy wyboru kasowego PIT a terminy wyboru formy opodatkowania",
// 	"liniowy/skala/ryczałt do 20-tego dnia kolejnego miesiąca po uzyskanym pierwszym przychodzie, ale nie później niż do końca roku podatkowego formy zawiadomienia naczelnika US, czyli ePuap, CEDiG, listownie",
// ];

const showToast = async (message: string): Promise<void> => {
	await Toast.show({
		text: message,
		duration: "long",
		position: "top",
	});
};

const openWebinarInBrowser = async (
	roomId: string | null,
	email: string | undefined
): Promise<void> => {
	if (!roomId || !email) {
		const message = !roomId
			? "Brak identyfikatora pokoju webinaru"
			: "Brak adresu email użytkownika";
		await showToast(message).catch((error_) => {
			console.error("Error showing toast", error_);
		});
		return;
	}

	try {
		const { webinar_token, webinar_slug } = await getWebinarRedirectData(
			roomId,
			email
		);

		const url = `https://akademialtca.clickmeeting.com/${webinar_slug}/${webinar_token}`;
		console.log("ClickMeeting URL:", url);

		await Browser.open({ url });
	} catch (error_) {
		if (error_ instanceof Error) {
			console.error("Error message:", error_.message);
			console.error("Error stack:", error_.stack);
		} else {
			console.error("Error string:", String(error_));
		}
		await showToast("Nie udało się otworzyć webinaru").catch((error__) => {
			console.error("Error showing toast", error__);
		});
	}
};

export const WorkshopStudy = (): JSX.Element => {
	const { studyWorkshopDetails } = useLoaderData({
		from: "/(app)/_app/study/$studyId",
	}) as unknown as { studyWorkshopDetails: StudyWorkshopDetails | undefined };
	const router = useRouter();
	const search = useSearch({
		from: "/(app)/_app/study/$studyId",
	});
	const [subscriptionLoader, setSubscriptionLoader] = useState(false);
	const [webinarLoader, setWebinarLoader] = useState(false);

	const webinarSubscription = useWebinarSubscription();
	const courseSubscription = useCourseSubscription();

	const { data: profileData } = useQuery({
		...getProfileOptions,
	});

	if (!studyWorkshopDetails) {
		return <div>Nie udało się załadować danych</div>;
	}
	const isUpcoming = search.status?.toLowerCase().includes("online");
	const isWebinar = search.status?.toLowerCase().includes("webinar");
	const isCourse =
		search.status?.toLowerCase().includes("szkolenie") ||
		search.status?.toLowerCase().includes("kurs");

	const accessState = determineAccessState(studyWorkshopDetails);

	return (
		<main className="py-3 pb-5 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			{studyWorkshopDetails === undefined && (
				<>
					<Skeleton className="h-[40px] mb-4" />
					<Skeleton className="h-[175px] mb-4" />
					<Skeleton className="h-[36px] mb-4" />
					<Skeleton className="h-[48px] mb-4" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
					<Skeleton className="h-[20px] mb-2" />
				</>
			)}
			{studyWorkshopDetails && (
				<>
					<div className="flex text-fontDarkGray pb-4">
						<button
							className="whitespace-nowrap flex-shrink-0"
							onClick={() => {
								router.history.back();
							}}
						>
							{setBreadcrumb(
								studyWorkshopDetails?.course?.type_field as StudyStatus
							)}
						</button>
						<span className="px-4">&#8226;</span>
						<span className="truncate min-w-0">
							{studyWorkshopDetails.course.name}
						</span>
					</div>
					<CoverCardNewVisual
						authors={studyWorkshopDetails.course.authors_users_data}
						categories={[studyWorkshopDetails?.course.tags]}
						coverId={studyWorkshopDetails.course.id}
						title={studyWorkshopDetails.course.name}
						coverDate={chooseCoverDate(
							studyWorkshopDetails.course.starts_at,
							studyWorkshopDetails.course.legal_status
						)}
						slug={studyWorkshopDetails.course.slug}
					/>

					{isUpcoming && isWebinar && accessState === "CAN_SIGN_UP" && (
						<Button
							className="w-full my-5"
							onClick={() => {
								setSubscriptionLoader(true);
								webinarSubscription.mutate(studyWorkshopDetails.course.slug, {
									onSuccess: () => {
										showToast("Zapisałeś się na wydarzenie").catch((error) => {
											console.error("Error showing toast", error);
										});
										setSubscriptionLoader(false);
									},
									onError: () => {
										showToast("Zapis na wydarzenie nie powiódł się").catch(
											(error) => {
												console.error("Error showing toast", error);
											}
										);
										setSubscriptionLoader(false);
									},
								});
							}}
						>
							<LoaderSpinner className="mr-3" size={6} />
							Wyślij email z wydarzeniem
						</Button>
					)}

					{isUpcoming && isCourse && accessState === "CAN_SIGN_UP" && (
						<Button
							className="w-full my-5"
							onClick={() => {
								setSubscriptionLoader(true);
								courseSubscription.mutate(studyWorkshopDetails.course.slug, {
									onSuccess: () => {
										showToast("Mail został wysłany").catch((error) => {
											console.error("Error showing toast", error);
										});
										setSubscriptionLoader(false);
									},
									onError: () => {
										showToast(
											"Nie udało się wysłać maila z przypomnieniem."
										).catch((error) => {
											console.error("Error showing toast", error);
										});
										setSubscriptionLoader(false);
									},
								});
							}}
						>
							{subscriptionLoader && (
								<LoaderSpinner borderColor="white" className="mr-3" size={6} />
							)}
							Wyślij email z przypomnieniem
						</Button>
					)}

					{!isUpcoming &&
						studyWorkshopDetails.courseClasses
							.sort((a, b) => a.order - b.order)
							.map((element) => {
								return (
									<MultimediaCard
										key={element.id}
										courseClassData={element}
										productData={{
											productName: studyWorkshopDetails.course.name,
											productType: isWebinar
												? "webinar"
												: isCourse
													? "training"
													: "unknown",
											authors: studyWorkshopDetails.course.authors_users_data,
											productSlug: studyWorkshopDetails.course.slug,
											productId: studyWorkshopDetails.course.id.toString(),
										}}
										// slug={studyWorkshopDetails.course.slug}
										// workshopId={studyWorkshopDetails.course.id.toString()}
									/>
								);
							})}

					{(accessState === "REQUIRES_PAYMENT" ||
						accessState === "UNAVAILABLE") && (
						<div className="text-center text-sm mt-5 text-rose-500">
							Nie masz dostępu do tego szkolenia
						</div>
					)}

					{accessState === "ASSIGNED_WAITING" && isUpcoming && (
						<>
							<div className="text-sm mt-5 ml-3 flex items-start justify-start gap-4">
								<CheckIcon className="flex-shrink-0 mt-1 h-4 w-4" />
								<div className="flex flex-col items-start">
									<span className="text-fontseconadry">
										Jesteś zapisany na to wydarzenie. Dostęp znajdziesz na
										swojej skrzynce e-mail
									</span>
									<Button
										className="p-0 underline text-link"
										onClick={() => {
											setSubscriptionLoader(true);
											courseSubscription.mutate(
												studyWorkshopDetails.course.slug,
												{
													onSuccess: () => {
														showToast("Mail został wysłany").catch((error) => {
															console.error("Error showing toast", error);
														});
														setSubscriptionLoader(false);
													},
													onError: () => {
														showToast(
															"Nie udało się wysłać maila z przypomnieniem."
														).catch((error) => {
															console.error("Error showing toast", error);
														});
														setSubscriptionLoader(false);
													},
												}
											);
										}}
										variant="link"
									>
										{subscriptionLoader && (
											<LoaderSpinner className="mr-3" size={4} />
										)}
										Wyślij email z przypomnieniem
									</Button>
								</div>
							</div>
							<Button
								className="w-full my-5"
								onClick={async () => {
									setWebinarLoader(true);
									await openWebinarInBrowser(
										studyWorkshopDetails.course.room_id,
										profileData?.user?.email
									);
									setWebinarLoader(false);
								}}
							>
								{webinarLoader && (
									<LoaderSpinner
										borderColor="white"
										className="mr-3"
										size={6}
									/>
								)}
								<span className="mr-4">
									{isCourse
										? "Otwórz szkolenie na ClickMeeting"
										: isWebinar
											? "Otwórz webinar na ClickMeeting"
											: "Otwórz na ClickMeeting"}
								</span>
								<ArrowIcon className="rotate-180" fillColor="#FFFFFF" />
							</Button>
						</>
					)}

					{studyWorkshopDetails.course?.media
						?.filter((elemenet) => !elemenet.mime_type.includes("image"))
						.map((element) => (
							<TrainingFile
								key={element.id}
								file={{ ...element, url: element.original_url }}
							/>
						))}

					{studyWorkshopDetails.userOwnsProduct && (
						<CoursesCertificats
							courseName={studyWorkshopDetails.course.name}
							courseId={studyWorkshopDetails.course.id}
						/>
					)}
					<h1 className="text-2xl font-bold py-4 pr-7">
						{studyWorkshopDetails.course.name}
					</h1>
					{studyWorkshopDetails.course.content && (
						<ExternalHtml
							html={studyWorkshopDetails.course.content}
							className="html-content text pr-7 mb-4"
						/>
					)}

					<AuthorsSimplyList
						authors={studyWorkshopDetails.course.authors_users_data}
					/>
					{/* <div className="flex flex-col w-full">
						<div className="flex gap-6 mb-4">
							<div className="flex flex-1 items-center">
								<img alt="clock" className="w-5 h-5 mr-2" src={clock} />
								<p>30 godzin</p>
							</div>
							<div className="flex flex-1 items-center">
								<img alt="star" className="w-5 h-5 mr-2" src={star} />
								<p>Certyfikat: Tak</p>
							</div>
						</div>
						<div className="flex gap-6">
							<div className="flex flex-1 items-center">
								<img alt="file" className="w-5 h-5 mr-2" src={file} />
								<p>Materiały: Tak</p>
							</div>
							<div className="flex flex-1 items-center">
								<img alt="question" className="w-5 h-5 mr-2" src={question} />
								<p>Stan prawny z dnia 01.10.2024</p>
							</div>
						</div>
					</div> */}
					{/* <WhatDidYouLearn data={WHAT_DID_YOU_LEARN_MOCK} /> */}
				</>
			)}
			{/* // TODO: podpiąć logikę wyświetlania analogicznie do Upcoming.tsx lecz pod prawidłowym endpointem */}
			{/* {WEBINARS_SLIDES.length > 0 ? (
				<EmblaCarouselMVP
					options={{ align: "start", loop: true }}
					slides={WEBINARS_SLIDES}
				/>
			) : (
				<Skeleton className="h-[215px] w-full pb-5 rounded-xl" />
			)} */}
		</main>
	);
};
